import { useState } from "react";
import { useContext } from "react";
import BetSlipContext from "../context/BetSlipContext";
import Keyboard from "../pages/MatchDetails/Keyboard";
import UnMatchedKeyboard from "./UnMatchedKeyboard";

/* ───────────────────────────────────────────────────────────────────────────
 * Reference-matched Open Bets table (kingexch111 style).
 * Scoped `obx-*` classes + inline styles so it never bleeds into theme CSS.
 *
 *   Back (Bet For)   Odds   Stake   Profit
 *   Lay (Bet Against)  Odds   Stake   Liability
 *
 * Back rows highlighted light-blue, Lay rows light-pink. Selection name bold,
 * sub-market name shown grey underneath.
 * ─────────────────────────────────────────────────────────────────────────── */

const COL = {
  odds: { flex: "0 0 60px", textAlign: "right", fontSize: "13px", color: "#1b2d38" },
  stake: { flex: "0 0 60px", textAlign: "right", fontSize: "13px", color: "#1b2d38" },
  profit: { flex: "0 0 72px", textAlign: "right", fontSize: "13px", color: "#1b2d38" },
};

const HeaderListing = ({ type, sortbyTime, secondType, type2 }) => {
  // left label
  let leftLabel;
  if (type === "fancy") leftLabel = sortbyTime ? "Yes/No" : secondType;
  else if (type === "Back") leftLabel = "Back (Bet For)";
  else if (type === "Lay") leftLabel = "Lay (Bet Against)";
  else leftLabel = "Back/Lay";

  const oddsLabel = type === "fancy" ? "Runs/Odds" : "Odds";
  const profitLabel =
    type === "Lay" ? "Liability" : type === "Back" ? "Profit" : type === "fancy" ? "Profit" : "Profit/Liability";

  return (
    <div
      className="obx-head"
      style={{
        display: "flex",
        alignItems: "center",
        padding: "7px 12px",
        background: "#fff",
        borderBottom: "1px solid #e4e4e4",
      }}
    >
      <span style={{ flex: 1, fontSize: "12px", color: "#555", fontWeight: 400 }}>{leftLabel}</span>
      <span style={{ ...COL.odds, color: "#555", fontWeight: 400 }}>{oddsLabel}</span>
      <span style={{ ...COL.stake, color: "#555", fontWeight: 400 }}>Stake</span>
      <span style={{ ...COL.profit, color: "#555", fontWeight: 400 }}>{profitLabel}</span>
      {type2 === "UnMatched" && <span style={{ flex: "0 0 26px" }} />}
    </div>
  );
};

const TableBody = ({ res, betinfo, helper, betType, type, clearBets, desktop }) => {
  let {
    betfairObj,
    setMessageBox,
    messaageBox,
    isBetPlaced,
    setIsBetPlaced,
    betSelectionObj,
    setBetSelectionObj,
    setBetFairObj,
    defaultStake,
    handelPlaceBetfairUpdate,
  } = useContext(BetSlipContext);
  const [id, setId] = useState("");

  const isFancy = betType === "Fancy" || res?.type === "Yes" || res?.type === "No";
  const isLayLike = isFancy ? res.type === "No" : res.betType === "lay";
  const rowBg = isLayLike ? "#f6d9e0" : "#d7e8f4"; // light-pink lay / light-blue back
  const pillLabel = isFancy ? (res.type === "Yes" ? "Yes" : "No") : res.betType === "lay" ? "Lay" : "Back";

  const selectionName = res?._sel || (isFancy ? res?.fancyName : res?.teamName);
  // Sub-market name under the selection (reference kingexch111 layout). Fancy
  // bets have no sub-market; match-odds/bookmaker carry `market` ("Match Odds").
  const subName =
    res?._sub ??
    (isFancy ? "" : res?.runnerName || res?.marketName || res?.market || res?.marketLabel || "");
  const oddsValue = isFancy ? `${res.betRun}/${res.bhav}` : res.bhav;
  const plValue = res?._pl ?? (res?.betType === "lay" || res?.type === "No" ? res?.loseAmount : res?.profitAmount);

  const isEditing =
    betSelectionObj?.priceToogle &&
    betSelectionObj?.priceToogleType === "unMatched" &&
    betSelectionObj?.betType === "betFair" &&
    betSelectionObj.id === res.matchBetId;

  return (
    <>
      {/* Reference id strip — shown ABOVE the bet row when Bet Info is on */}
      {betinfo && (
        <div
          style={{
            display: "flex",
            gap: "14px",
            padding: "4px 12px",
            background: "#dce9f4",
            fontSize: "12px",
            color: "#3b5160",
          }}
        >
          <span>Ref: {res.matchBetId}</span>
          <span>{helper?.dateFormat?.(res.timeInserted)}</span>
        </div>
      )}
      <div
        className="obx-row"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          background: rowBg,
          borderBottom: "1px solid #c7dced",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "10px" }}>
          {type === "UnMatched" ? (
            <a
              id="editBtn"
              className={isEditing ? "a-edit select" : "a-edit"}
              onClick={() => {
                setBetSelectionObj({
                  priceToogle: true,
                  bidPrice: res.amount ? res.amount : "",
                  betType: "betFair",
                  oddsSelect: res.bhav,
                  priceToogleType: "unMatched",
                  layorback: res.betType,
                  minAmount: 1,
                  maxAmount: 100,
                  minAmountText: false,
                  id: res.matchBetId,
                });
                setId(res.matchBetId);
              }}
              href="javascript:void(0)"
            >
              Edit
            </a>
          ) : desktop ? (
            /* Desktop (reference): Back/Lay/Yes/No as a border badge-free pill —
               just a darker solid background, no border line. */
            <span
              style={{
                flex: "0 0 auto",
                fontSize: "11px",
                fontWeight: 400,
                color: isLayLike ? "#5e2331" : "#14304a",
                background: isLayLike ? "#eab6c2" : "#94bede",
                borderRadius: "6px",
                padding: "3px 12px",
              }}
            >
              {pillLabel}
            </span>
          ) : (
            /* Plain Back/Lay/Yes/No label — no colored pill (reference) */
            <span
              style={{
                flex: "0 0 40px",
                fontSize: "13px",
                fontWeight: 400,
                color: "#1b2d38",
              }}
            >
              {pillLabel}
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
            <span
              style={{
                fontSize: desktop ? "11px" : "13px",
                fontWeight: 700,
                color: "#1b2d38",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectionName}
            </span>
            {subName && (
              <span
                style={{
                  fontSize: desktop ? "11px" : "12px",
                  fontWeight: 400,
                  color: "#8a98a2",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subName}
              </span>
            )}
          </div>
        </div>

        <span style={{ ...COL.odds }}>{oddsValue}</span>
        <span style={{ ...COL.stake }}>{res.amount}</span>
        <span style={{ ...COL.profit }}>{plValue}</span>

        {type === "UnMatched" && (
          <span
            onClick={() => clearBets(res.matchBetId)}
            style={{ flex: "0 0 26px", textAlign: "right", cursor: "pointer" }}
          >
            <a href="javascript:void(0)" className="trashcan" />
          </span>
        )}
      </div>

      {isEditing ? <UnMatchedKeyboard res={res} /> : ""}
    </>
  );
};

const MatchedTab = () => (
  <div
    className="obx-tabs"
    style={{
      display: "flex",
      background: "#fff",
      padding: "8px 12px 0",
    }}
  >
    <span
      style={{
        padding: "8px 18px",
        fontSize: "14px",
        fontWeight: 700,
        color: "#111",
        background: "#f1f1f1",
        borderTopLeftRadius: "4px",
        borderTopRightRadius: "4px",
      }}
    >
      Matched
    </span>
    <span style={{ flex: 1, background: "#d9dcde", borderTopRightRadius: "4px" }} />
  </div>
);

const FancyListing = ({ sortbyTime, filtered, betinfo, helper }) => {
  return (
    <>
      <MatchedTab />
      {sortbyTime ? (
        <div id="txnList" className="slip-list" style={{ display: "block" }}>
          <HeaderListing type="fancy" sortbyTime={sortbyTime} />
          {filtered?.map((res, i) => (
            <TableBody key={i} res={res} betinfo={betinfo} helper={helper} betType="Fancy" />
          ))}
        </div>
      ) : (
        <div id="txnList" className="slip-list" style={{ display: "block" }}>
          {filtered?.some((res) => res.type === "Yes") && (
            <>
              <HeaderListing type={"fancy"} sortbyTime={sortbyTime} secondType={"Yes"} />
              {filtered?.map(
                (res, i) =>
                  res.type === "Yes" && (
                    <TableBody key={i} res={res} betinfo={betinfo} helper={helper} betType="Fancy" />
                  )
              )}
            </>
          )}
          {filtered?.some((res) => res.type === "No") && (
            <>
              <HeaderListing type="fancy" sortbyTime={sortbyTime} secondType={"No"} />
              {filtered?.map(
                (res, i) =>
                  res.type === "No" && (
                    <TableBody key={i} res={res} betinfo={betinfo} betType="Fancy" helper={helper} />
                  )
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

const SportsBookListing = ({ sortbyTime, filtered, betinfo, helper }) => {
  return (
    <>
      <MatchedTab />
      <div id="txnList" className="slip-list" style={{ display: "block" }}>
        <div
          className="obx-head"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "7px 12px",
            background: "#fff",
            borderBottom: "1px solid #e4e4e4",
          }}
        >
          <span style={{ flex: 1, fontSize: "12px", color: "#555" }}>Back (Bet For)</span>
          <span style={{ ...COL.odds, color: "#555" }}>Odds</span>
          <span style={{ ...COL.stake, color: "#555" }}>Stake</span>
          <span style={{ ...COL.profit, color: "#555" }}>Profit</span>
        </div>
        {filtered?.map((res, i) => (
          <div
            key={i}
            className="obx-row"
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              background: "#d7e8f4",
              borderBottom: "1px solid #c7dced",
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ flex: "0 0 40px", fontSize: "13px", fontWeight: 400, color: "#1b2d38" }}>
                Back
              </span>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#1b2d38" }}>{res?.fancyName}</span>
                <span style={{ fontSize: "12px", color: "#8a98a2" }}>{res?.runnerName}</span>
              </div>
            </div>
            <span style={{ ...COL.odds }}>{res.bhav}</span>
            <span style={{ ...COL.stake }}>{res.amount}</span>
            <span style={{ ...COL.profit }}>{res.profitAmount}</span>
            {betinfo && (
              <div style={{ flex: "1 1 100%", fontSize: "11px", color: "#3b5160", paddingTop: "4px" }}>
                <span style={{ marginRight: "12px" }}>Ref: {res.matchBetId}</span>
                <span>{helper?.dateFormat?.(res.timeInserted)}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export { HeaderListing, TableBody, FancyListing, SportsBookListing, MatchedTab };
